import { useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { injectDogfoodWidget, rememberStaff, removeDogfoodWidget } from './dogfoodLoader';

/**
 * Dogfood: embed Toado's own bug-report widget inside the Toado app, so we
 * can capture Toado bugs with Toado (report a bug from any app page, hand it
 * off into a ticket in the internal Toado project).
 *
 * The single load-bearing gate is staff-or-dev: the viewer is a staff user,
 * OR this is a dev build. A production build must NOT expose an internal
 * bug-report launcher (and the console/network collectors behind it) to every
 * customer, so staff-only keeps it to us.
 *
 * The widget KEY always resolves (see dogfoodKey.ts: a build-time
 * `VITE_TOADO_WIDGET_KEY` override, else a baked public prod fallback), so
 * whether the widget injects is decided purely by that staff-or-dev gate, not
 * by whether an env var happens to be set. The key is public by design (it
 * authorizes only report-submission to one project).
 *
 * Since TOADO-910 the widget's listeners usually start before this runs, from
 * `earlyDogfoodWidget.ts`, on a browser whose last sign-in was staff; no
 * launcher shows and nothing is sent until this says so. It settles it the
 * moment auth resolves: staff is remembered for the next page load and gets
 * the loader, which keeps those listeners; anyone else, signed out included,
 * has the flag cleared and, outside a dev build, the listeners and any widget
 * taken off the page at once (see `removeDogfoodWidget`). A remembered flag
 * only starts listening early; it never shows the widget to a session that is
 * not staff (Codex, rounds 1 and 2; z.ai, round 1).
 */
export function DogfoodWidget(): null {
  const { user, loading } = useAuth();
  const staff = user?.isStaff === true;

  useEffect(() => {
    if (loading) return;
    rememberStaff(staff);
    if (!staff && !import.meta.env.DEV) removeDogfoodWidget();
    // Loaded at most once (the loader's own guard and the element id): a
    // StrictMode double-mount or a re-render must not boot a second launcher.
    if (import.meta.env.DEV || staff) injectDogfoodWidget();
  }, [loading, staff]);

  return null;
}
